import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  Search, 
  Phone, 
  Mail, 
  Car, 
  DollarSign,
  Edit,
  Trash2,
  ChevronRight,
  Download,
  Upload,
  Tag,
  X,
  Filter
} from 'lucide-react';

const Contacts = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [contacts, setContacts] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tagFilter, setTagFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    tags: []
  });

  useEffect(() => {
    fetchContacts();
    fetchTags();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/contacts/tags`);
      setAllTags(response.data);
    } catch (error) {
      console.error('Failed to load tags');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, formData);
        toast.success('Contact updated successfully');
      } else {
        await axios.post(`${API}/contacts`, formData);
        toast.success('Contact created successfully');
      }
      
      setShowAddModal(false);
      setEditingContact(null);
      setFormData({ name: '', email: '', phone: '', notes: '', tags: [] });
      fetchContacts();
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      email: contact.email || '',
      phone: contact.phone || '',
      notes: contact.notes || '',
      tags: contact.tags || []
    });
    setShowAddModal(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/contacts/${deleteId}`);
      toast.success('Contact deleted successfully');
      setDeleteId(null);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleAddRide = (contact) => {
    navigate('/rides/new', { 
      state: { 
        prefillContact: {
          client_name: contact.name,
          client_email: contact.email,
          client_phone: contact.phone
        }
      }
    });
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/contacts/export/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contacts.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Contacts exported successfully');
    } catch (error) {
      toast.error('Failed to export contacts');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/contacts/import/csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        console.log('Import errors:', response.data.errors);
      }
      
      fetchContacts();
      fetchTags();
      setShowImportModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import contacts');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleJSONImport = async () => {
    if (!importData.trim()) {
      toast.error('Please enter contact data');
      return;
    }

    setImporting(true);
    try {
      // Parse the import data (support both JSON array and line-by-line format)
      let contacts = [];
      
      try {
        // Try JSON parse first
        contacts = JSON.parse(importData);
        if (!Array.isArray(contacts)) {
          contacts = [contacts];
        }
      } catch {
        // If not JSON, parse as line-by-line (Name, Email, Phone)
        const lines = importData.split('\n').filter(l => l.trim());
        contacts = lines.map(line => {
          const parts = line.split(',').map(p => p.trim());
          return {
            name: parts[0] || '',
            email: parts[1] || '',
            phone: parts[2] || '',
            notes: parts[3] || '',
            tags: parts[4] ? parts[4].split(';').map(t => t.trim()) : []
          };
        });
      }

      const response = await axios.post(`${API}/contacts/import`, { contacts });
      toast.success(response.data.message);
      
      fetchContacts();
      fetchTags();
      setShowImportModal(false);
      setImportData('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import contacts');
    } finally {
      setImporting(false);
    }
  };

  const addTagToForm = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const removeTagFromForm = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tagToRemove) });
  };

  const filteredContacts = contacts.filter(contact => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        contact.name?.toLowerCase().includes(query) ||
        contact.email?.toLowerCase().includes(query) ||
        contact.phone?.includes(query) ||
        contact.tags?.some(t => t.toLowerCase().includes(query))
      );
      if (!matchesSearch) return false;
    }
    
    // Tag filter
    if (tagFilter !== 'all') {
      if (!contact.tags?.includes(tagFilter)) return false;
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading contacts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-8 h-8 text-blue-600" />
            Contacts
          </h2>
          <p className="text-gray-600 mt-1">
            Manage your clients and view their ride history
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button 
            variant="outline"
            onClick={handleExportCSV}
            data-testid="export-contacts-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowImportModal(true)}
            data-testid="import-contacts-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button 
            onClick={() => {
              setEditingContact(null);
              setFormData({ name: '', email: '', phone: '', notes: '', tags: [] });
              setShowAddModal(true);
            }}
            data-testid="add-contact-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="contact-search"
          />
        </div>
        {allTags.length > 0 && (
          <div className="w-full sm:w-48">
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger data-testid="tag-filter">
                <Tag className="w-4 h-4 mr-2 text-gray-500" />
                <SelectValue placeholder="Filter by tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                {allTags.map(tag => (
                  <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Stats Bar */}
      {contacts.length > 0 && (
        <div className="flex gap-4 text-sm text-gray-600">
          <span>{filteredContacts.length} of {contacts.length} contacts</span>
          {tagFilter !== 'all' && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <Tag className="w-3 h-3" />
              {tagFilter}
              <button onClick={() => setTagFilter('all')} className="ml-1 hover:text-red-600">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      {/* Contacts List */}
      {filteredContacts.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchQuery || tagFilter !== 'all' ? 'No contacts found' : 'No contacts yet'}
              </h3>
              <p className="text-gray-500 mb-4">
                {searchQuery || tagFilter !== 'all'
                  ? 'Try adjusting your filters' 
                  : 'Add your first client contact to get started'}
              </p>
              {!searchQuery && tagFilter === 'all' && (
                <Button onClick={() => setShowAddModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Contact
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredContacts.map((contact) => (
            <Card 
              key={contact.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              data-testid={`contact-card-${contact.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex justify-between items-start mb-3">
                  <div 
                    className="flex-1 cursor-pointer"
                    onClick={() => navigate(`/contacts/${contact.id}`)}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      {contact.name}
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </h3>
                    {contact.email && (
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <Mail className="w-3 h-3" />
                        {contact.email}
                      </p>
                    )}
                    {contact.phone && (
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3" />
                        {contact.phone}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(contact);
                      }}
                      className="h-8 w-8 p-0"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteId(contact.id);
                      }}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Tags */}
                {contact.tags && contact.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {contact.tags.map(tag => (
                      <Badge 
                        key={tag} 
                        variant="secondary" 
                        className="text-xs cursor-pointer hover:bg-blue-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTagFilter(tag);
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Stats */}
                <div className="flex gap-4 mb-4">
                  <div className="flex items-center gap-1 text-sm">
                    <Car className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">{contact.total_rides || 0}</span>
                    <span className="text-gray-500">rides</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="font-medium">${(contact.total_spent || 0).toFixed(2)}</span>
                    <span className="text-gray-500">total</span>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/contacts/${contact.id}`);
                    }}
                  >
                    View History
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddRide(contact);
                    }}
                    data-testid={`add-ride-${contact.id}`}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Ride
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Contact Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? 'Edit Contact' : 'Add New Contact'}
            </DialogTitle>
            <DialogDescription>
              {editingContact 
                ? 'Update contact information' 
                : 'Add a new client contact to your list'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Client name"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag (e.g., VIP, Corporate)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTagToForm())}
                    data-testid="contact-tag-input"
                  />
                  <Button type="button" variant="outline" onClick={addTagToForm}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {formData.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                        {tag}
                        <button type="button" onClick={() => removeTagFromForm(tag)} className="hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                {allTags.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">Existing tags:</p>
                    <div className="flex flex-wrap gap-1">
                      {allTags.filter(t => !formData.tags.includes(t)).slice(0, 5).map(tag => (
                        <Badge 
                          key={tag} 
                          variant="outline" 
                          className="text-xs cursor-pointer hover:bg-blue-50"
                          onClick={() => setFormData({ ...formData, tags: [...formData.tags, tag] })}
                        >
                          + {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any notes about this client"
                  data-testid="contact-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-contact-btn">
                {editingContact ? 'Save Changes' : 'Add Contact'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Contacts</DialogTitle>
            <DialogDescription>
              Import contacts from a CSV file or paste data directly
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* CSV Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-2">
                Upload a CSV file with columns: Name, Email, Phone, Tags, Notes
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                {importing ? 'Importing...' : 'Choose CSV File'}
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or paste data</span>
              </div>
            </div>

            {/* Manual Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paste contacts (one per line: Name, Email, Phone, Notes, Tags)
              </label>
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="John Smith, john@email.com, 555-1234, VIP client, Corporate;Airport&#10;Jane Doe, jane@email.com, 555-5678, Regular customer, VIP"
                className="w-full h-32 px-3 py-2 border rounded-md text-sm"
                data-testid="import-data-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Separate tags with semicolons (;)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleJSONImport} 
              disabled={importing || !importData.trim()}
              data-testid="import-submit-btn"
            >
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this contact? This action cannot be undone.
              Note: This will not delete any rides associated with this contact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Contacts;
